import { useContext, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DatafySwitchContext } from '~/shared/context/DatafySwitchProvider';
import { SelectOption } from '~/shared/utils/types';
import { getApiFilters, splitAccountFilters } from '~/shared/utils/apiFilters';
import useUtilizationQueryParams from '~/shared/components/UtilizationCharts/useUtilizationQueryParams';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { isOrganizationView, getOrganizationAccounts } from '~/shared/utils/accountUtils';
import { appendAccountsScope } from '~/shared/utils/organizationUtils';

import {
  apiFilterKey,
  utilizationDateFilterKey,
  utilizationFullGraphKey,
  utilizationRefreshStateKey,
} from '../../common/serachParamKeys';
import { useVolumesQuery } from '../useVolumesQuery';

import FleetManagerTable from './AllVolumesTable/AllVolumesTable';

const dataLimit = 50;
const sortKey = 'sort_field';

export default function AllVolumesPage() {
  const { accounts, activeTenantId } = useAuthContext();
  const [searchParams] = useSearchParams();
  const { datafySwitchVolumes, removeDatafySwitchVolume } = useContext(DatafySwitchContext);
  const isOrgView = isOrganizationView(activeTenantId, accounts);

  const activeDateFilter = searchParams.get(utilizationDateFilterKey);
  const activeRefreshState = searchParams.get(utilizationRefreshStateKey);
  const apiFilters = searchParams.get(apiFilterKey);

  const sortField: { field: string; dir: 'desc' | 'asc' } = JSON.parse(
    searchParams.get(sortKey) as string
  );

  const [pageOffset, setPageOffset] = useState('0');

  const { utilizationQueryUrl } = useUtilizationQueryParams({
    utilizationDateFilterSearchParam: activeDateFilter,
    showFullGraphSearchParam: searchParams.get(utilizationFullGraphKey),
    apiFiltersSearchParam: apiFilters,
    refreshStateSearchParams: activeRefreshState,
  });

  const qs = new URLSearchParams({
    sortBy: JSON.stringify({
      field: sortField?.field || 'volumeId',
      ascending: sortField?.dir !== 'desc',
    }),
    limit: dataLimit.toString(),
    offset: pageOffset,
  });

  const filters = JSON.parse(apiFilters || '[]') as SelectOption[];
  const { accountIds, filtersWithoutAccount } = useMemo(() => splitAccountFilters(filters), [filters]);
  const appliedAccountFilters = filters.filter((f) => f.group === 'accountId');

  const orgAccounts = useMemo(
    () => getOrganizationAccounts(activeTenantId, accounts, isOrgView),
    [activeTenantId, accounts, isOrgView]
  );

  const parsedFiltersResult = getApiFilters(filtersWithoutAccount);
  const parsedFiltersSearchParams: string[] = Array.isArray(parsedFiltersResult)
    ? parsedFiltersResult
    : [parsedFiltersResult];

  const savingsQs = new URLSearchParams([...parsedFiltersSearchParams.map((f) => ['filters', f])]);
  appendAccountsScope(savingsQs, accounts, activeTenantId, accountIds);

  const savingsQueryUrl = `/api/v1/volumes/savings-stats?${savingsQs.toString()}`;

  const volumesQuery = useVolumesQuery({
    qs,
    filters,
    sortField,
    pageOffset,
    savingsChartQueryKey: savingsQueryUrl,
    utilizationChartQueryKey: utilizationQueryUrl,
    datafySwitchVolumes,
    removeDatafySwitchVolume,
    isOrgView,
    appliedAccountFilters,
  });

  const tableData = volumesQuery.data
    ? { volumes: volumesQuery.data.volumes, count: volumesQuery.data.count ?? 0 }
    : undefined;

  const isInitialLoading = volumesQuery.isLoading && !volumesQuery.data;

  return (
    <FleetManagerTable
      setPageOffset={setPageOffset}
      pageOffset={pageOffset}
      sortField={sortField}
      data={tableData}
      loading={isInitialLoading}
      isTableFetching={volumesQuery.isFetching}
      accounts={orgAccounts}
    />
  );
}
